import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearch } from "@tanstack/react-router";
import { TrajectoryView } from "./TrajectoryView";
import { fetchJobDetail } from "./api";

interface Trial {
  name: string;
  task_name: string;
  verifier_result: {
    rewards: {
      reward: number;
    };
  };
  agent_result: {
    n_input_tokens: number;
    n_output_tokens: number;
    n_cache_tokens: number;
  };
  trajectory: any;
  agentAnswer: string | null;
  expectedAnswer: string | null;
  testOutput: string | null;
}

interface JobDetail {
  id: string;
  started_at: string;
  finished_at: string;
  trials: Trial[];
}

/**
 * Extract number from trial name for sorting.
 */
function extractTrialNumber(name: string): number {
  const match = name.match(/fhiragentbench-(\d+)/);
  return match && match[1] ? parseInt(match[1], 10) : 0;
}

/**
 * Sort trials numerically by the number in their name.
 */
function sortTrials(trials: any[]): any[] {
  return [...trials].sort((a, b) => extractTrialNumber(a.name) - extractTrialNumber(b.name));
}

export function JobDetail() {
  const { jobId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }
    fetchJobDetail(jobId)
      .then(data => {
        setJob(data);
        setLoading(false);
        
        // If no trial in URL and we have trials, navigate to first trial
        if (!search.trial && data.trials && data.trials.length > 0) {
          const sortedTrials = sortTrials(data.trials);
          navigate({
            to: "/jobs/$jobId",
            params: { jobId },
            search: { trial: sortedTrials[0].name },
            replace: true, // Replace current history entry instead of adding new one
          });
        }
        // If trial in URL doesn't exist in trials, navigate to first trial
        else if (search.trial && data.trials && !data.trials.find((t: any) => t.name === search.trial)) {
          const sortedTrials = sortTrials(data.trials);
          navigate({
            to: "/jobs/$jobId",
            params: { jobId },
            search: { trial: sortedTrials[0].name },
            replace: true,
          });
        }
      })
      .catch(err => {
        console.error("Failed to load job:", err);
        setLoading(false);
      });
  }, [jobId]); // Only depend on jobId to avoid loops
  
  if (!jobId) {
    return <div className="text-center py-8 text-red-400">Job ID is required</div>;
  }

  if (loading) {
    return <div className="text-center py-8">Loading job details...</div>;
  }

  if (!job) {
    return <div className="text-center py-8 text-red-400">Job not found</div>;
  }

  const selectedTrial = search.trial;
  const currentTrial = job.trials.find(t => t.name === selectedTrial);
  const sortedTrials = sortTrials(job.trials);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => navigate({ to: "/" })}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors border border-gray-300"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold">{jobId}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
            <h3 className="font-semibold mb-2">Job Info</h3>
            <div className="text-sm space-y-1 text-gray-700">
              <div>Started: {new Date(job.started_at).toLocaleString()}</div>
              <div>Finished: {new Date(job.finished_at).toLocaleString()}</div>
              <div>Trials: {job.trials.length}</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <h3 className="font-semibold mb-2">Trials</h3>
            <div className="space-y-2">
              {sortedTrials.map(trial => {
                const reward = trial.verifier_result?.rewards?.reward ?? 0;
                const isSelected = selectedTrial === trial.name;
                return (
                  <div
                    key={trial.name}
                    onClick={() => navigate({
                      to: "/jobs/$jobId",
                      params: { jobId: jobId! },
                      search: { trial: trial.name },
                    })}
                    className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                      isSelected
                        ? "bg-blue-50 border-blue-300"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="text-sm font-medium">{trial.task_name}</div>
                      <div
                        className={`text-sm font-bold ${
                          reward === 1 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {reward === 1 ? "✓" : "✗"}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{trial.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {currentTrial ? (
            <TrialDetail trial={currentTrial} />
          ) : (
            <div className="text-center py-8 text-gray-600">Select a trial to view details</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Format duration in milliseconds to a human-readable string.
 */
function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(2)}s`;
  } else {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(2);
    return `${minutes}m ${seconds}s`;
  }
}

/**
 * Calculate total trial duration from trajectory steps.
 */
function calculateTrialDuration(trajectory: any): number {
  if (!trajectory?.steps || trajectory.steps.length === 0) {
    return 0;
  }
  const firstTimestamp = new Date(trajectory.steps[0].timestamp).getTime();
  const lastTimestamp = new Date(trajectory.steps[trajectory.steps.length - 1].timestamp).getTime();
  return lastTimestamp - firstTimestamp;
}

/**
 * Calculate total completion tokens by summing all step completion tokens.
 */
function calculateTotalCompletionTokens(trajectory: any): number {
  if (!trajectory?.steps) {
    return 0;
  }
  return trajectory.steps.reduce((sum: number, step: any) => {
    return sum + (step.metrics?.completion_tokens || 0);
  }, 0);
}

/**
 * Calculate total prompt tokens by summing all step prompt tokens.
 */
function calculateTotalPromptTokens(trajectory: any): number {
  if (!trajectory?.steps) {
    return 0;
  }
  return trajectory.steps.reduce((sum: number, step: any) => {
    return sum + (step.metrics?.prompt_tokens || 0);
  }, 0);
}

function TrialDetail({ trial }: { trial: Trial }) {
  const reward = trial.verifier_result?.rewards?.reward ?? 0;
  const question = trial.trajectory?.steps?.[0]?.message || "No question found";
  const questionMatch = question.match(/# Question\n\n(.*?)(?:\n\n|$)/s);
  const questionText = questionMatch ? questionMatch[1].trim() : question;
  const totalDuration = calculateTrialDuration(trial.trajectory);
  
  // Calculate tokens - prefer final_metrics, fallback to summing steps, then agent_result
  const totalCompletionTokens = 
    trial.trajectory?.final_metrics?.total_completion_tokens ?? 
    (calculateTotalCompletionTokens(trial.trajectory) || trial.agent_result?.n_output_tokens || 0);
  
  const totalPromptTokens = 
    trial.trajectory?.final_metrics?.total_prompt_tokens ?? 
    (calculateTotalPromptTokens(trial.trajectory) || trial.agent_result?.n_input_tokens || 0);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold">{trial.task_name}</h2>
          <div
            className={`px-3 py-1 rounded-full text-sm font-semibold ${
              reward === 1
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {reward === 1 ? "PASSED" : "FAILED"}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Question</h3>
            <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm whitespace-pre-wrap">
              {questionText}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Agent's Answer</h3>
              <div
                className={`bg-gray-50 border border-gray-200 rounded p-3 text-sm font-mono ${
                  reward === 1 ? "text-green-700" : "text-red-700"
                }`}
              >
                {trial.agentAnswer || "No answer provided"}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Expected Answer</h3>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm font-mono text-blue-700">
                {trial.expectedAnswer
                  ? trial.expectedAnswer.replace(/\[\[|\]\]/g, "").replace(/'/g, "")
                  : "Not available"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-xs text-gray-600 mb-1">Total Duration</div>
              <div className="text-lg font-semibold text-gray-900">
                {totalDuration > 0 ? formatDuration(totalDuration) : "N/A"}
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-xs text-gray-600 mb-1">Steps</div>
              <div className="text-lg font-semibold text-gray-900">
                {trial.trajectory?.final_metrics?.total_steps || 0}
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-xs text-gray-600 mb-1">Input Tokens</div>
              <div className="text-lg font-semibold text-gray-900">
                {totalPromptTokens.toLocaleString()}
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-xs text-gray-600 mb-1">Output Tokens</div>
              <div className="text-lg font-semibold text-gray-900">
                {totalCompletionTokens.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {trial.testOutput && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Test Results</h3>
          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-96 overflow-y-auto">
            {trial.testOutput}
          </div>
        </div>
      )}

      {trial.trajectory && (
        <TrajectoryView trajectory={trial.trajectory} />
      )}
    </div>
  );
}
